import React, { useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import DataList from '../../../form/DataList';
import Input from '../../../form/Input';
import { useMRTState } from '../../../../hooks/useMRTState';
import { getMRTDefaultProps } from '../../../../helpers/mrt/getMRTDefaultProps';

export default function ChapasTab({ modulo: m, eleicaoUuid, permissoes, parametro, isMobile, bloqueado }) {
    const mrtChapas = useMRTState({ storageKey: 'Eleicao.Chapas', pageSizeDefault: Number(parametro?.paginacao) || 5 });
    const mrtMembros = useMRTState({ storageKey: 'Eleicao.ChapaMembros', pageSizeDefault: Number(parametro?.paginacao) || 5 });
    const defaultsChapas = getMRTDefaultProps({ mrt: mrtChapas, parametro, isMobile });
    const defaultsMembros = getMRTDefaultProps({ mrt: mrtMembros, parametro, isMobile });
    const ocupado = m.ocupado || bloqueado,
        disabled = ocupado || !m.podeAlterar;
    const columns = useMemo(
        () => [
            {
                id: 'chapas',
                header: 'Cadastro da chapas Diretoria ou Conselho Fiscal',
                columns: [
                    { accessorKey: 'numero', header: 'Número', size: 80 },
                    { accessorKey: 'nome', header: 'Chapa' },
                    { id: 'grupo', header: 'Grupo', accessorFn: (r) => (r.grupo === 'fiscal' ? 'Conselho Fiscal' : 'Diretoria') },
                    {
                        accessorKey: 'dataInscricao',
                        header: 'Inscrição',
                        Cell: ({ cell }) => cell.getValue()?.split('-').reverse().join('/') || '',
                    },
                    { id: 'quantidade', header: 'Membros', size: 90, accessorFn: (r) => r.membros?.length || 0 },
                ],
            },
        ],
        []
    );
    const columnsMembros = useMemo(
        () => [
            {
                id: 'chapas',
                header: 'Cadastro da membros da Diretoria ou Conselho Fiscal',
                columns: [
                    { accessorKey: 'ordem', header: 'Ordem', size: 70 },
                    { accessorKey: 'Pessoa.descricao', header: 'Pessoa' },
                    { accessorKey: 'rhCargo.descricao', header: 'Cargo' },
                    { accessorKey: 'UnidadeCondominial.descricao', header: 'Unidade' },
                    {
                        id: 'autorizacao',
                        header: 'Precisa de autorização',
                        accessorFn: (r) => (r.necessitaAutorizacao ? 'Sim' : 'Não'),
                    },
                    {
                        id: 'autorizante',
                        header: 'Quem autoriza',
                        accessorFn: (r) => (r.necessitaAutorizacao ? r.Autorizante?.descricao || 'Verificado ao salvar' : ''),
                    },
                ],
            },
        ],
        []
    );
    const membrosOrdenados = useMemo(
        () => [...m.item.membros].sort((a, b) => Number(a.ordem) - Number(b.ordem)),
        [m.item.membros]
    );
    if (!eleicaoUuid)
        return (
            <Alert severity="info" sx={{ my: 2 }}>
                Salve a eleição e abra novamente para cadastrar as chapas.
            </Alert>
        );
    return (
        <Stack spacing={2} sx={{ py: 2, minWidth: 0 }}>
            <Alert severity="info">As chapas são salvas nesta aba. Cada pessoa pode ocupar apenas um cargo nesta eleição.</Alert>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                    type="button"
                    variant="outlined"
                    disabled={ocupado || m.pendente || !permissoes.imprimir || !m.lista.length}
                    onClick={() => m.imprimir(false)}
                >
                    Imprimir todas as chapas
                </Button>
                <Button
                    type="button"
                    variant="outlined"
                    disabled={ocupado || m.pendente || !permissoes.imprimir || !m.item.uuid}
                    onClick={() => m.imprimir(true)}
                >
                    Imprimir chapa selecionada
                </Button>
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                    type="button"
                    variant="outlined"
                    disabled={ocupado || m.pendente || !permissoes.imprimir || !m.lista.length}
                    onClick={() => m.imprimir(false, 'qualificacao')}
                >
                    Qualificação de todas as chapas
                </Button>
                <Button
                    type="button"
                    variant="outlined"
                    disabled={ocupado || m.pendente || !permissoes.imprimir || !m.item.uuid}
                    onClick={() => m.imprimir(true, 'qualificacao')}
                >
                    Qualificação da selecionada
                </Button>
            </Stack>
            <TextField
                label="Referência do estatuto/regimento para a autorização (opcional)"
                size="small"
                multiline
                maxRows={3}
                value={m.referenciaAutorizacao}
                onChange={m.alterarReferencia}
                disabled={ocupado || !permissoes.imprimir}
                inputProps={{ maxLength: 600 }}
                placeholder="Ex.: conforme Art. 38 do Estatuto Social e Art. 19 do Regimento Eleitoral"
                helperText="Usada apenas nesta impressão de autorizações; confira os artigos aplicáveis à eleição."
            />
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                    type="button"
                    variant="outlined"
                    disabled={
                        ocupado ||
                        m.pendente ||
                        !permissoes.imprimir ||
                        !m.lista.some((c) => c.membros?.some((v) => v.necessitaAutorizacao))
                    }
                    onClick={() => m.imprimir(false, 'autorizacoes')}
                >
                    Autorizações de todas as chapas
                </Button>
                <Button
                    type="button"
                    variant="outlined"
                    disabled={
                        ocupado ||
                        m.pendente ||
                        !permissoes.imprimir ||
                        !m.item.uuid ||
                        !m.item.membros.some((v) => v.necessitaAutorizacao)
                    }
                    onClick={() => m.imprimir(true, 'autorizacoes')}
                >
                    Autorizações da selecionada
                </Button>
            </Stack>
            <Typography variant="body2">A impressão usa os dados já salvos. Cada chapa começa em uma nova página.</Typography>
            {m.erro && (
                <Alert
                    severity="error"
                    action={
                        <Button type="button" disabled={ocupado} onClick={m.carregar}>
                            Recarregar
                        </Button>
                    }
                >
                    {m.erro}
                </Alert>
            )}
            <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                <MaterialReactTable
                    {...defaultsChapas}
                    columns={columns}
                    data={m.lista}
                    getRowId={(r) => r.uuid}
                    manualFiltering={false}
                    manualSorting={false}
                    manualPagination={false}
                    rowCount={undefined}
                    enableRowSelection={false}
                    autoResetPageIndex
                    state={{ ...defaultsChapas.state, isLoading: m.carregando }}
                    renderRowActions={
                        isMobile
                            ? undefined
                            : ({ row }) => (
                                  <Button
                                      type="button"
                                      disabled={ocupado || m.pendente}
                                      onClick={() => m.selecionar(row.original)}
                                  >
                                      Selecionar
                                  </Button>
                              )
                    }
                    renderRowActionMenuItems={
                        isMobile
                            ? ({ row, closeMenu }) => [
                                  <MenuItem
                                      key="selecionar"
                                      disabled={ocupado || m.pendente}
                                      onClick={() => {
                                          closeMenu();
                                          m.selecionar(row.original);
                                      }}
                                  >
                                      Selecionar
                                  </MenuItem>,
                              ]
                            : undefined
                    }
                />
            </Box>
            <Stack direction="row" alignItems="center" useFlexGap flexWrap="wrap" spacing={1}>
                <Typography variant="h6">{m.item.uuid ? `Chapa ${m.item.numero}` : 'Nova chapa'}</Typography>
                <Button type="button" disabled={ocupado} onClick={m.novo}>
                    {m.pendente ? 'Descartar edição' : m.item.uuid ? 'Limpar seleção' : 'Limpar'}
                </Button>
            </Stack>
            {!m.podeAlterar && (
                <Alert severity="info">Consulta sem permissão para {m.item.uuid ? 'alterar esta' : 'incluir uma'} chapa.</Alert>
            )}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '110px minmax(200px, 1fr) 200px' }, gap: 2 }}>
                <TextField
                    label="Número"
                    name="numero"
                    type="number"
                    size="small"
                    value={m.item.numero}
                    onChange={m.alterar}
                    disabled={disabled}
                    inputProps={{ min: 1, step: 1 }}
                />
                <TextField
                    label="Nome da chapa"
                    name="nome"
                    size="small"
                    value={m.item.nome}
                    onChange={m.alterar}
                    disabled={disabled}
                    inputProps={{ maxLength: 255 }}
                />
                <TextField
                    select
                    label="Grupo"
                    name="grupo"
                    size="small"
                    value={m.item.grupo}
                    onChange={m.alterar}
                    disabled={disabled}
                >
                    <MenuItem value="diretoria">Diretoria</MenuItem>
                    <MenuItem value="fiscal">Conselho Fiscal</MenuItem>
                </TextField>
                <TextField
                    label="Data da inscrição"
                    name="dataInscricao"
                    type="date"
                    size="small"
                    value={m.item.dataInscricao || ''}
                    onChange={m.alterar}
                    disabled={disabled}
                    InputLabelProps={{ shrink: true }}
                    sx={{ gridColumn: { sm: 'span 2' }, maxWidth: { sm: 230 } }}
                />
            </Box>
            <TextField
                label="Observação"
                name="observacao"
                value={m.item.observacao || ''}
                onChange={m.alterar}
                disabled={disabled}
                size="small"
                multiline
                minRows={2}
            />
            <Divider />
            <Typography variant="h6">Membros da chapa</Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    alignItems: 'flex-end',
                    '& > .eleicao-campo > *': { margin: 0 },
                    '& > .eleicao-campo input': { boxSizing: 'border-box', height: '34px', marginBottom: 0 },
                }}
            >
                <Box className="eleicao-campo" sx={{ width: { xs: '100%', sm: 340 }, maxWidth: '100%' }}>
                    <DataList
                        inputWidth="100%"
                        text="Pessoa"
                        name="pessoa"
                        value={m.membro.pessoa}
                        filteredOptions={m.opcoes.pessoa}
                        handleOnChange={m.alterarMembro}
                        readonly={disabled}
                        placeholder="Nome ou CPF (mínimo 2 caracteres)"
                    />
                </Box>
                <Box className="eleicao-campo" sx={{ width: { xs: '100%', sm: 260 }, maxWidth: '100%' }}>
                    <DataList
                        inputWidth="100%"
                        text="Cargo"
                        name="cargo"
                        value={m.membro.cargo}
                        filteredOptions={m.opcoes.cargo}
                        handleOnChange={m.alterarMembro}
                        readonly={disabled}
                        placeholder="Digite pelo menos 2 caracteres"
                    />
                </Box>
                <Box className="eleicao-campo" sx={{ width: 100 }}>
                    <Input
                        inputWidth="100%"
                        text="Ordem"
                        type="number"
                        name="ordem"
                        value={m.membro.ordem}
                        handleOnChange={m.alterarMembro}
                        readonly={disabled}
                    />
                </Box>
                <Box className="eleicao-campo" sx={{ width: { xs: '100%', sm: 340 }, maxWidth: '100%' }}>
                    <DataList
                        inputWidth="100%"
                        text="Unidade condominial"
                        name="unidade"
                        value={m.membro.unidade}
                        filteredOptions={m.opcoes.unidade}
                        handleOnChange={m.alterarMembro}
                        readonly={disabled}
                        placeholder="Código ou descrição da unidade"
                    />
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', '& .MuiButton-root': { minHeight: '34px', py: 0.5 } }}>
                    <Button type="button" variant="contained" disabled={disabled} onClick={m.adicionarMembro}>
                        {m.editandoMembro ? 'Atualizar membro' : 'Adicionar membro'}
                    </Button>
                    <Button type="button" disabled={ocupado} onClick={m.limparMembro}>
                        Limpar membro
                    </Button>
                </Box>
            </Box>
            <FormControlLabel
                control={
                    <Checkbox
                        name="necessitaAutorizacao"
                        checked={m.membro.necessitaAutorizacao}
                        onChange={m.alterarMembro}
                        disabled={disabled}
                    />
                }
                label="Membro indicado — necessita de autorização do associado"
            />
            {m.membro.necessitaAutorizacao && (
                <Alert severity="info">
                    Selecione a unidade. Ao salvar, o sistema identifica o responsável financeiro associado que autoriza a
                    participação.
                </Alert>
            )}
            <Typography variant="body2">
                Adicionar ou remover membros altera a composição abaixo. Clique em Salvar chapa para gravar.
            </Typography>
            <Box sx={{ minWidth: 0, maxWidth: '100%' }}>
                <MaterialReactTable
                    {...defaultsMembros}
                    columns={columnsMembros}
                    data={membrosOrdenados}
                    getRowId={(r) => String(r.pessoaId)}
                    manualFiltering={false}
                    manualSorting={false}
                    manualPagination={false}
                    rowCount={undefined}
                    enableRowSelection={false}
                    autoResetPageIndex
                    renderRowActions={
                        isMobile
                            ? undefined
                            : ({ row }) => (
                                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                      <IconButton
                                          type="button"
                                          title="Editar membro"
                                          disabled={disabled || m.membroPendente}
                                          onClick={() => m.editarMembro(row.original)}
                                      >
                                          <Edit />
                                      </IconButton>
                                      <IconButton
                                          type="button"
                                          title="Remover membro"
                                          color="error"
                                          disabled={disabled || m.membroPendente}
                                          onClick={() => m.removerMembro(row.original)}
                                      >
                                          <Delete />
                                      </IconButton>
                                  </Box>
                              )
                    }
                    renderRowActionMenuItems={
                        isMobile
                            ? ({ row, closeMenu }) => [
                                  <MenuItem
                                      key="editar"
                                      disabled={disabled || m.membroPendente}
                                      onClick={() => {
                                          closeMenu();
                                          m.editarMembro(row.original);
                                      }}
                                  >
                                      Editar membro
                                  </MenuItem>,
                                  <MenuItem
                                      key="remover"
                                      disabled={disabled || m.membroPendente}
                                      onClick={() => {
                                          closeMenu();
                                          m.removerMembro(row.original);
                                      }}
                                  >
                                      Remover membro
                                  </MenuItem>,
                              ]
                            : undefined
                    }
                />
            </Box>
            {m.membroPendente && (
                <Alert severity="warning">Adicione/atualize o membro ou limpe sua edição antes de salvar a chapa.</Alert>
            )}
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                    type="button"
                    color="success"
                    variant="contained"
                    disabled={disabled || !m.sujo || m.membroPendente || Boolean(m.erro)}
                    onClick={m.salvar}
                >
                    Salvar chapa
                </Button>
                <Button
                    type="button"
                    color="error"
                    disabled={ocupado || m.pendente || !m.item.uuid || !permissoes.cancelar}
                    onClick={m.excluir}
                >
                    Excluir chapa
                </Button>
            </Stack>
        </Stack>
    );
}
